import './utils/pendo'
import './utils/datadog'

import Vue from 'vue'
import AsyncComputed from '@/plugins/AsyncComputed/'
import router from './router'
import store from './store'
import actions from './store/actions'
import App from './components/app-root'
import { jwt, hasRole } from '@/utils/jwt.js'
import Loading from 'vue-loading-overlay'
import moment from 'moment'
import ClickOutside from '@/directives/click-outside'
import Tooltip from '@/directives/tooltip'
import Dialog from '@/directives/dialog'
import FileDropTarget from '@/directives/file-drop-target'
import Popover from '@/directives/popover'
import Resize from '@/directives/resize.js'
import TrackScroll from '@/directives/TrackScroll'
import AutoHeightIframe from '@/directives/auto-height-iframe'
import { EventBus as bus } from '@/utils/eventBus'
import { addEventListener as addRequestEventListener } from './utils/request'

import { ClientTable as MtTable, BaseTable as MTTableBase, Column as MtTableColumn } from '@/components/shared/mt-table'

moment.updateLocale('en', {
    relativeTime: {
        future: 'in %s',
        past: '%s ago',
        s: 'a few seconds',
        ss: '%d seconds',
        m: '1 minute',
        mm: '%d minutes',
        h: '1 hour',
        hh: '%d hours',
        d: '1 day',
        dd: '%d days',
        M: '1 month',
        MM: '%d months',
        y: '1 year',
        yy: '%d years',
    },
})

var meta = new (class {
    set title(title) {
        if (title) {
            title = ' - ' + title
        } else {
            title = ''
        }

        document.title = 'ManagementTrack' + title
    }
})()

Vue.use(AsyncComputed)

Vue.directive('auto-height-iframe', AutoHeightIframe)
Vue.directive(ClickOutside.name, ClickOutside.wireup)
Vue.directive(Dialog.name, Dialog.wireup)
Vue.directive('file-drop-target', FileDropTarget)
Vue.directive('popover', Popover)
Vue.directive(Resize.name, Resize.wireup)
Vue.directive(Tooltip.name, Tooltip.wireup)
Vue.directive(TrackScroll.name, TrackScroll.wireup)

const flash = (message, status, options) =>
    store.dispatch(actions.flash, Object.assign({}, options, { message, status }))

Vue.prototype.$bus = bus
Vue.prototype.$flash = flash
Vue.prototype.$meta = meta
Vue.prototype.$resolveRoute = function (route) {
    const query = this.$route.query
    const site = {}

    Object.keys(query)
        .filter(k => k.startsWith('site.'))
        .forEach(k => (site[k] = query[k]))

    if (!route.query) {
        route.query = site
    } else {
        route.query = {
            ...site,
            ...route.query,
        }
    }

    return this.$router.resolve(route)
}

import Breadcrumbs from './components/shared/breadcrumbs'
import Modal from './components/shared/modal'
import MtCheckbox from './components/shared/mt-checkbox'
import MtInput from './components/shared/mt-input'
import MtRadioButton from './components/shared/mt-radio-button'
import MtRadioButtonGroup from './components/shared/mt-radio-button-group'
import MtSelect from './components/shared/mt-select'
import MtSwitch from './components/shared/mt-switch'
import TabbedView from './components/Tabs/tabbed-view'

Vue.component('breadcrumbs', Breadcrumbs)
Vue.component('modal', Modal)
Vue.component('Modal', Modal)
Vue.component('mt-checkbox', MtCheckbox)
Vue.component('mt-input', MtInput)
Vue.component('mt-radio-button', MtRadioButton)
Vue.component('mt-radio-button-group', MtRadioButtonGroup)
Vue.component('mt-select', MtSelect)
Vue.component('mt-switch', MtSwitch)
Vue.component('mt-table', MtTable)
Vue.component('mt-table-base', MTTableBase)
Vue.component('mt-table-column', MtTableColumn)
Vue.component('tabbed-view', TabbedView)
Vue.component('TabbedView', TabbedView)
Vue.component('loading', Loading)

router.beforeEach((to, from, next) => {
    // Close the left drawer/sheet but leave the right drawer open
    store.dispatch(actions.closePopover, null)
    store.dispatch(actions.closeSearchModal)

    // redirect to the login page if the route requires the user to be logged in
    if (!to.meta.anonymous && !jwt.authenticated) {
        next(
            `/login?redirect=${encodeURIComponent(
                document.location.pathname + document.location.search + document.location.hash
            )}`
        )
        return
    }

    // Redirect to the route's specified 'redirect' page (or the dashboard page by default)
    // if the user tries to access a route they have no access to
    // this is a convenience only; roles are enforced by the apis as well
    if (to.meta.roles && !to.meta.roles.find(hasRole)) {
        next({ path: to.meta?.redirect ?? '/' })
        return
    }

    meta.title = to.meta.title

    next()
})

addRequestEventListener('unauthorized', () => {
    if (!(router.currentRoute.meta.anonymous ?? false)) {
        document.location = `/login?redirect=${encodeURIComponent(
            document.location.pathname + document.location.search + document.location.hash
        )}`
    }
})

const app = new Vue({
    store,
    router,
    render: h => h(App),
    provide: {
        store,
        bus,
        flash,
    },
})

export { app, router, store }
